import { zodResolver } from '@hookform/resolvers/zod';
import { endOfDay } from 'date-fns';
import React, { useState } from 'react';
import { useEffect } from 'react';
import { Controller, FormProvider, UseFormSetValue, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { GroupBase, OptionProps, components } from 'react-select';

import ROWarningModal from './components/ROWarningModal';
import { NewTicketFormValuesT } from './declarations';
import useOpenNewTicketPopup from './hooks/useOpenNewTicketPopup';
import {
  ActionBtnStyled,
  CtaContainerStyled,
  FlexContainerMessageStyled,
  FormElementWrapStyled,
  FormRowWrapStyled,
  FormStyled,
  TypographyTitleStyled,
  getPopupContentStyle,
} from './OpenNewTicketPopup.styled';
import validationSchema from './validationSchema';
import Button from 'components/Button/Button';
import ErrorSection from 'components/ErrorSection/ErrorSection';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import { FormDatePicker, FormInput, FormSelect } from 'components/Form';
import FormTextArea from 'components/Form/FormTextArea/FormTextArea';
import Popup from 'components/Popup/Popup';
import PriorityIcon from 'components/PriorityIcon/PriorityIcon';
import SuccessSection from 'components/SuccessSection/SuccessSection';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { SelectOptionT } from 'declarations/types/select';

const OpenNewTicketPopup = () => {
  /**
   * Hooks
   */
  const {
    requestCategoriesOptions,
    claimStatusesOptions,
    isLoadingRoofTopOptions,
    roofTopIdsOptions,
    ticketPrioritiesOptions,
    isLoadingTicketPrioritiesOptions,
    handleCloseNewTicketPopup,
    handleSubmitForm,
    handleAmountBlur,
    isPopupOpened,
    isTicketSuccessfullyCreated,
    showOpenTicketForm,
    isTicketCreationError,
    handleRoofTopChange,
    handleNavigateToExistingTicketDetails,
    ticketRequestCategories,
    handleLineNumberChange,
    handleROChange,
    lineItemsObj,
    setAdvisorNumber,
    setRecentRoData,
    advisorNumber,
    checkIfRoExists,
    createTicketErrorMessage,
    createTicketExistingId,
    createTicketValidationErrorMessage,
    isCreatingNewTicket,
    isLoadingClaimStatusesOptions,
    isLoadingRequestCategoriesOptions,
    setShowLineNumberDropdown,
    showLineNumberDropdown,
    isRoWarningPopupOpen,
    setIsRoWarningPopupOpen,
    lineNumbers,
    setLineNumbers,
    setSelectedRoofTopId,
  } = useOpenNewTicketPopup();

  /**
   * Hooks
   */
  const [techNumber, setTechNumber] = useState<string | undefined>(undefined);
  const [singleTicketMessage, setSingleTicketMessage] = useState<string>('');
  const [, setSelectedSingleCategory] = useState<string | number>();

  const { isMobile } = useResponsive();
  const fromMethods = useForm<NewTicketFormValuesT>({
    defaultValues: {
      lineNumber: null,
      amount: '0.00',
      text: '',
      subCategoryId: null,
    },
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });
  let [searchParams] = useSearchParams();

  /**
   * Variables
   */
  const { handleSubmit, setValue, reset, getValues } = fromMethods;
  const datePickerMaxDate = endOfDay(new Date());
  const selectedRoofTopId = searchParams.get(TicketsRefinementKeysE.RoofTopId);

  /**
   * Effects
   */
  useEffect(() => {
    if (isPopupOpened && getValues('roofTopId') === undefined && selectedRoofTopId !== null) {
      setValue('roofTopId', selectedRoofTopId);
      setSelectedRoofTopId(selectedRoofTopId);
    }
  });

  useEffect(() => {
    if (isPopupOpened) {
      setValue('ticketPriorityId', 3);
    }
  });

  useEffect(() => {
    if (isPopupOpened && roofTopIdsOptions.length === 1) {
      const roofTopId = roofTopIdsOptions[0].value as string;
      setValue('roofTopId', roofTopId);
      setSelectedRoofTopId(roofTopId);
    }

    if (isPopupOpened && lineNumbers.length === 1) {
      const lineNumber = lineNumbers[0].value as string;
      setValue('lineNumber', showLineNumberDropdown ? lineNumber : null);
      setValue('techNumber', lineItemsObj[0]?.techNumber);
    }
  }, [
    roofTopIdsOptions,
    isPopupOpened,
    setValue,
    lineItemsObj,
    lineNumbers,
    showLineNumberDropdown,
    handleLineNumberChange,
    setSelectedRoofTopId,
  ]);

  useEffect(
    () => () => {
      if (isPopupOpened) {
        reset();
        setSingleTicketMessage('');
      }
    },
    [isPopupOpened, reset]
  );

  useEffect(() => {
    if (!isPopupOpened) {
      setIsRoWarningPopupOpen(false);
      setShowLineNumberDropdown(false);
      setLineNumbers([]);
      setAdvisorNumber(undefined);
      setRecentRoData({
        roofTopId: null,
        roNumber: null,
      });
      if (!selectedRoofTopId) {
        setSelectedRoofTopId(undefined);
      }
    }
  }, [
    isPopupOpened,
    setAdvisorNumber,
    setIsRoWarningPopupOpen,
    setLineNumbers,
    setRecentRoData,
    setShowLineNumberDropdown,
    selectedRoofTopId,
    setSelectedRoofTopId,
  ]);

  /**
   * Handlers
   */
  const handleRoNumberBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    const roNumber = event.target.value;
    const { roofTopId } = getValues();
    handleROChange(roNumber);
    checkIfRoExists(roofTopId, roNumber, setValue);
  };
  const handleRoInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleROChange(event.target.value);
  };
  const onLineNumberChange = (value: any) => {
    handleLineNumberChange(value);
    const selectedLineItem = lineItemsObj.find(item => item.lineNumber === value);
    setTechNumber(selectedLineItem ? selectedLineItem?.techNumber : '');
  };

  const onLineNumberInputChange = (value: any) => {
    handleLineNumberChange(value);
  };

  const onRoofTopChange = (value: any) => {
    handleRoofTopChange(value);
  };

  const { Option } = components;
  const OptionWithPriorityIcon = (
    props: JSX.IntrinsicAttributes & OptionProps<SelectOptionT, false, GroupBase<SelectOptionT>>
  ) => (
    <Option {...props}>
      <PriorityIcon priority={(props.data as SelectOptionT).label} />
      {(props.data as SelectOptionT).label}
    </Option>
  );

  const handleChangeCategory = (value: any) => {
    setSelectedSingleCategory(value);

    const textFieldValue = getValues('text');

    if (!textFieldValue || textFieldValue === '') {
      const foundCategory = ticketRequestCategories?.find(category => category.id === value);
      setSingleTicketMessage(foundCategory?.defaultMessage || '');
    }
  };

  const handleRequestCategoryChange = (
    value: any,
    setValue: UseFormSetValue<NewTicketFormValuesT>,
    setSingleTicketMessage: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const selectedCategory = ticketRequestCategories?.find(category => category.id === value);
    if (selectedCategory) {
      const message = selectedCategory.defaultMessage || '';
      setValue('text', message);
      setSingleTicketMessage(message);
    }
  };

  return (
    <Popup
      showCloseBtn
      popupId={ScrollLockIdE.OpenNewTicketPopup}
      contentStyle={getPopupContentStyle(isMobile)}
      isOpen={isPopupOpened}
      onClose={handleCloseNewTicketPopup}
      closeOnEscape={false}
      closeOnDocumentClick={!isRoWarningPopupOpen}
    >
      {showOpenTicketForm && (
        <FlexContainer direction="column" align="center">
          <TypographyTitleStyled variant="h2" color="dark">
            New Ticket
          </TypographyTitleStyled>
          {isRoWarningPopupOpen && (
            <ROWarningModal isOpen={isRoWarningPopupOpen} setIsOpen={setIsRoWarningPopupOpen} />
          )}

          <FormProvider {...fromMethods}>
            <FormStyled role="form" onSubmit={handleSubmit(handleSubmitForm)}>
              <FormElementWrapStyled>
                <FormSelect<NewTicketFormValuesT>
                  isRequired
                  label="Choose Dealership"
                  name="roofTopId"
                  placeholder="Choose Dealership"
                  options={roofTopIdsOptions}
                  isLoading={isLoadingRoofTopOptions}
                  onValueChange={onRoofTopChange}
                />
              </FormElementWrapStyled>
              <FormRowWrapStyled>
                <FormElementWrapStyled>
                  <FormInput
                    isRequired
                    label="Enter RO Number"
                    name="roNumber"
                    placeholder="RO Number"
                    onBlur={handleRoNumberBlur}
                    onInput={handleRoInput}
                  />
                </FormElementWrapStyled>
                <FormElementWrapStyled>
                  <FormInput
                    label="Enter Advisor Number"
                    name="advisorNumber"
                    placeholder="Advisor Number"
                    defaultValue={advisorNumber ? advisorNumber : ''}
                  />
                </FormElementWrapStyled>
              </FormRowWrapStyled>
              <FormRowWrapStyled>
                <FormElementWrapStyled>
                  {showLineNumberDropdown ? (
                    <FormSelect
                      label="Select Line Number"
                      name="lineNumber"
                      placeholder="Select Line Number"
                      options={lineNumbers}
                      inputValue=""
                      onValueChange={onLineNumberChange}
                    />
                  ) : (
                    <FormInput
                      label="Enter Line Number"
                      name="lineNumber"
                      placeholder="Line Number"
                      onChange={onLineNumberInputChange}
                    />
                  )}
                </FormElementWrapStyled>
                <FormElementWrapStyled>
                  <FormInput
                    label="Enter Tech Number"
                    name="techNumber"
                    placeholder="Tech Number"
                    defaultValue={techNumber ? techNumber : ''}
                  />
                </FormElementWrapStyled>
              </FormRowWrapStyled>
              <FormRowWrapStyled>
                <FormElementWrapStyled>
                  <FormDatePicker
                    isRequired
                    label="Choose RO Date"
                    name="roDate"
                    placeholder="RO Date"
                    maxDate={datePickerMaxDate}
                  />
                </FormElementWrapStyled>
                <FormElementWrapStyled>
                  <FormInput
                    label="Enter Amount $"
                    name="amount"
                    placeholder="Amount $"
                    onBlur={handleAmountBlur(setValue)}
                  />
                </FormElementWrapStyled>
              </FormRowWrapStyled>
              <FormRowWrapStyled>
                <FormElementWrapStyled>
                  <FormSelect
                    isRequired
                    label="Choose Claim status"
                    name="claimStatusId"
                    placeholder="Claim statuses"
                    options={claimStatusesOptions}
                    isLoading={isLoadingClaimStatusesOptions}
                  />
                </FormElementWrapStyled>
                <FormElementWrapStyled>
                  <FormSelect
                    isRequired
                    label="Choose Ticket priority"
                    name="ticketPriorityId"
                    placeholder="Ticket priorities"
                    options={ticketPrioritiesOptions}
                    isLoading={isLoadingTicketPrioritiesOptions}
                    components={{ Option: OptionWithPriorityIcon }}
                  />
                </FormElementWrapStyled>
              </FormRowWrapStyled>
              <FormRowWrapStyled>
                <FormElementWrapStyled>
                  <Controller
                    name="requestCategoryId"
                    control={fromMethods.control}
                    render={({ field }) => {
                      return (
                        <FormSelect
                          isRequired
                          label="Choose Request category"
                          name="requestCategoryId"
                          placeholder="Request categories"
                          options={requestCategoriesOptions}
                          maxMenuHeight={180}
                          isLoading={isLoadingRequestCategoriesOptions}
                          noOptionsMessage={({ inputValue }) =>
                            inputValue ? 'No options' : 'Choose Dealership'
                          }
                          onValueChange={value => {
                            field.onChange(value);
                            handleRequestCategoryChange(value, setValue, setSingleTicketMessage);
                          }}
                        />
                      );
                    }}
                  />
                </FormElementWrapStyled>
                <FormElementWrapStyled>
                  <FormDatePicker
                    label="Choose Claim Submitted Date"
                    name="claimSubmittedDate"
                    placeholder="Claim Submitted Date"
                    maxDate={datePickerMaxDate}
                  />
                </FormElementWrapStyled>
              </FormRowWrapStyled>
              <FormRowWrapStyled>
                <FormElementWrapStyled>
                  <Controller
                    name="text"
                    control={fromMethods.control}
                    render={({ field }) => {
                      const value = field.value || '';
                      return (
                        <FormTextArea
                          rows={1}
                          label="Enter Message"
                          name="text"
                          placeholder="Message"
                          value={singleTicketMessage || value}
                          onChange={e => {
                            handleChangeCategory(getValues('requestCategoryId'));
                            setSingleTicketMessage(e.target.value);
                            field.onChange(e);
                          }}
                        />
                      );
                    }}
                  />
                </FormElementWrapStyled>
              </FormRowWrapStyled>
              <CtaContainerStyled justify={isMobile ? 'center' : 'flex-end'}>
                <ActionBtnStyled
                  type="button"
                  variant="outline"
                  size="xl"
                  onClick={handleCloseNewTicketPopup}
                >
                  Cancel
                </ActionBtnStyled>
                <ActionBtnStyled size="xl" isLoading={isCreatingNewTicket}>
                  Create
                </ActionBtnStyled>
              </CtaContainerStyled>
            </FormStyled>
          </FormProvider>
        </FlexContainer>
      )}

      {isTicketSuccessfullyCreated && (
        <FlexContainerMessageStyled direction="column">
          <SuccessSection message="Ticket successfully created" />
        </FlexContainerMessageStyled>
      )}

      {isTicketCreationError && (
        <FlexContainerMessageStyled direction="column">
          <ErrorSection
            errorDetails={createTicketErrorMessage || createTicketValidationErrorMessage}
          />
          {createTicketExistingId && (
            <>
              <br />
              <Button onClick={handleNavigateToExistingTicketDetails}>
                Navigate to ticket details
              </Button>
            </>
          )}
        </FlexContainerMessageStyled>
      )}
    </Popup>
  );
};

export default OpenNewTicketPopup;
